import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { StoreItemPlaceholder } from '@atlas/components';
import { NotificationManager } from '@atlas/core';
import { Button, Icon, Label } from '@atlas/ui';

import ModalPortal from 'common/ModalPortal/ModalPortal';

import { FAILED_PURCHASE_TOAST, resolvePurchaseToast } from './storePurchaseErrors';

const PURCHASE_TIMEOUT_MS = 10000;

const StorePurchaseConfirmationPopup = ({ data = {}, id }) => {
  const { image, item_id, name } = data;
  const [isLoading, setIsLoading] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  const handleClose = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const notify = useCallback(({ isSuccess, ...toast }) => {
    NotificationManager.toast(toast);

    return isSuccess;
  }, []);

  const handleBuyNow = useCallback(async () => {
    if (isLoading) return;

    // `buyStoreItem` is a class method that reads `this.api`, so it has to stay
    // attached to the Smartico api object. Calling a detached reference throws.
    const smarticoApi = window._smartico?.api;

    if (typeof smarticoApi?.buyStoreItem !== 'function') {
      notify(FAILED_PURCHASE_TOAST);
      NotificationManager.removeNotification(id);

      return;
    }

    setIsLoading(true);

    let timeoutId;

    try {
      const result = await Promise.race([
        smarticoApi.buyStoreItem(item_id),
        new Promise((_, reject) => {
          timeoutId = setTimeout(reject, PURCHASE_TIMEOUT_MS);
        }),
      ]);

      if (notify(resolvePurchaseToast(result))) {
        NotificationManager.removeNotification(id);
      }
    } catch {
      notify(FAILED_PURCHASE_TOAST);
    } finally {
      clearTimeout(timeoutId);
      setIsLoading(false);
    }
  }, [id, item_id, isLoading, notify]);

  return (
    <ModalPortal backdropOff className="store-purchase-confirmation-z">
      <div className="store-purchase-confirmation">
        <button
          aria-label="close"
          className="store-purchase-confirmation__backdrop"
          onClick={handleClose}
          type="button"
        />
        <div className="store-purchase-confirmation__sheet">
          <div className="store-purchase-confirmation__header">
            <button
              className="store-purchase-confirmation__close"
              onClick={handleClose}
              type="button"
            >
              <Icon height={24} name="close" width={24} />
            </button>
          </div>

          <div className="store-purchase-confirmation__content">
            <h3 className="store-purchase-confirmation__title">{name}</h3>

            <div className="store-purchase-confirmation__image">
              {image && !hasImageError ? (
                <img
                  alt={name}
                  height={100}
                  onError={() => setHasImageError(true)}
                  src={image}
                  width={100}
                />
              ) : (
                <StoreItemPlaceholder className="store-purchase-confirmation__placeholder" />
              )}
            </div>

            <Label
              className="store-purchase-confirmation__message"
              message="store_purchase_confirmation_message"
            />
          </div>

          <div className="store-purchase-confirmation__buttons">
            <Button className="secondary big block" label="cancel" onClick={handleClose} />
            <Button
              className="primary big block"
              disabled={isLoading}
              label="store_purchase_buy_now"
              loading={isLoading}
              onClick={handleBuyNow}
            />
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

StorePurchaseConfirmationPopup.propTypes = {
  data: PropTypes.shape({
    image: PropTypes.string,
    item_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default StorePurchaseConfirmationPopup;
